import { useEffect, useRef, useState } from 'react';

const SECRET_ENTRANCE_WHISPER_RADIUS_TILES = 5;
const SECRET_ENTRANCE_WHISPER_COOLDOWN_MS = 9000;
const SECRET_ENTRANCE_WHISPER_VOLUME = 0.18;

const DUNGEON_MUSIC_POOL = ['dungeon', 'dungeon2', 'dungeon3', 'dungeon4', 'dungeon5', 'dungeon6', 'dungeon7'];
const BOSS_FLOOR_EXPLORATION_MUSIC_BASE = 'boss_exploration';
const SPECIAL_FLOOR_BOSS_ENEMY_IDS = new Set(['nested_horror', 'monsignor', 'prime_vessel', 'blackhand']);

const COMBAT_THEME_BY_DUNGEON = {
  dungeon: 'combat_theme',
  dungeon2: 'combat_theme2',
  dungeon3: 'combat_theme3',
  dungeon4: 'combat_theme4',
  dungeon5: 'combat_theme5',
  dungeon6: 'combat_theme6',
  dungeon7: 'combat_theme7',
};

const SPIDER_CRAWL_MIN_DELAY_MS = 5200;
const SPIDER_CRAWL_MAX_DELAY_MS = 8200;
const SPIDER_CRAWL_MIN_VOLUME = 0.22;
const SPIDER_CRAWL_MAX_VOLUME = 0.42;
const SPIDER_CRAWL_MIN_RADIUS = 1.4;
const SPIDER_CRAWL_MAX_RADIUS = 3.0;

const LOW_SANITY_AUDIO_SP_THRESHOLD = 20;
const HUD_GLITCH_START_PCT = 0.30;
const SANITY_AMBIENCE_DELAY_ANCHORS = [
  { pct: 1.0, minMs: 60000, maxMs: 80000 },
  { pct: 0.75, minMs: 40000, maxMs: 60000 },
  { pct: 0.5, minMs: 20000, maxMs: 35000 },
  { pct: 0.25, minMs: 16000, maxMs: 24000 },
  { pct: 0.15, minMs: 8000, maxMs: 18000 },
  { pct: 0.07, minMs: 6000, maxMs: 18000 },
  { pct: 0.0, minMs: 6000, maxMs: 18000 },
];
const SANITY_AMBIENCE_POOL_SIZE = 45;

function canPlayOgg() {
  try {
    const test = document.createElement('audio');
    return (
      !!test.canPlayType &&
      test.canPlayType('audio/ogg; codecs="vorbis"') !== ''
    );
  } catch {
    return false;
  }
}

function buildMusicSrc(baseName) {
  return canPlayOgg() ? `/audio/${baseName}.ogg` : `/audio/${baseName}.mp3`;
}

function shuffleDungeonMusicPool() {
  const pool = [...DUNGEON_MUSIC_POOL];

  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  return pool;
}

function pickNextDungeonBase() {
  const lastBase = window.__dd_dungeonMusicLastBase || null;
  let remainingPool = Array.isArray(window.__dd_dungeonMusicRemainingPool)
    ? [...window.__dd_dungeonMusicRemainingPool]
    : [];

  if (!remainingPool.length) {
    remainingPool = shuffleDungeonMusicPool();

    if (remainingPool.length > 1 && remainingPool[0] === lastBase) {
      const swapIdx = remainingPool.findIndex((base) => base !== lastBase);
      if (swapIdx > 0) {
        [remainingPool[0], remainingPool[swapIdx]] = [remainingPool[swapIdx], remainingPool[0]];
      }
    }
  }

  const base = remainingPool.shift() || DUNGEON_MUSIC_POOL[0] || 'dungeon';

  window.__dd_dungeonMusicRemainingPool = remainingPool;
  window.__dd_dungeonMusicLastBase = base;
  return base;
}

function getCombatBaseForDungeon(dungeonBase) {
  const activeCombatEnemyId = String(window.__dd_combatEnemyId || '').trim().toLowerCase();

  if (activeCombatEnemyId.startsWith('yahaka_voidfeeder')) {
    return 'combat_theme_yahaka';
  }

  return COMBAT_THEME_BY_DUNGEON[dungeonBase] || 'combat_theme';
}

function getActiveCombatEnemyId() {
  if (typeof window === 'undefined') return '';
  return String(window.__dd_combatEnemyId || '').trim().toLowerCase();
}

function isSpecialFloorBossCombatActive() {
  const activeCombatEnemyId = getActiveCombatEnemyId();
  return SPECIAL_FLOOR_BOSS_ENEMY_IDS.has(activeCombatEnemyId);
}

export default function useDungeonAudio({
  bootReady,
  isCombatActive,
  isSpiderBossFloorActive,
  isMonsignorBossFloorActive = false,
  isSpecialBossFloorActive = false,
  suppressCombatMusic = false,
  isStorylineActive,
  turns,
  currentFloorTorchDuration,
  tarotNoTorchesNeeded,
  playerPos,
  secretEntrancePos,
  sanity,
  maxSp,
  sanityPct,
  madness,
  sanityTier,
}) {
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [secretWhisperTorchFlicker, setSecretWhisperTorchFlicker] = useState(null);
  const [sanityScreenFlicker, setSanityScreenFlicker] = useState(null);
  const [sanityDistortionSpike, setSanityDistortionSpike] = useState(null);
  const [sanityPeripheralShadow, setSanityPeripheralShadow] = useState(null);

  const [sanityCollapseBurst, setSanityCollapseBurst] = useState(null);
  const [sanityHudGlitchBurst, setSanityHudGlitchBurst] = useState(null);

  const secretWhisperTorchFlickerTimeoutRef = useRef(null);
  const secretWhisperLastAtRef = useRef(0);
  const secretWhisperLastIdxRef = useRef(0);

  const sanityScreenFlickerTimeoutRef = useRef(null);
  const sanityWhisperLoopTimeoutRef = useRef(null);
  const sanityFlickerLoopTimeoutRef = useRef(null);

  const sanityWhisperLastAtRef = useRef(0);
  const sanityWhisperLastIdxRef = useRef(0);

  const sanityDistortionTimeoutRef = useRef(null);
  const sanityDistortionLoopTimeoutRef = useRef(null);

  const sanityPeripheralShadowTimeoutRef = useRef(null);
  const sanityPeripheralShadowLoopTimeoutRef = useRef(null);

  const sanityCollapseBurstTimeoutRef = useRef(null);
  const sanityCollapseBurstLoopTimeoutRef = useRef(null);

  const sanityHudGlitchBurstTimeoutRef = useRef(null);
  const sanityHudGlitchLoopTimeoutRef = useRef(null);

  const positionalHallucinationLoopTimeoutRef = useRef(null);
  const movingSanityActiveNodesRef = useRef([]);
  const lowSanitySfxLastIdxRef = useRef(0);
  const spiderCrawlLoopTimeoutRef = useRef(null);
  const spiderCrawlActiveNodesRef = useRef([]);
  const spiderCrawlLastIdxRef = useRef(0);
  const audioContextRef = useRef(null);
  const decodedBufferCacheRef = useRef(new Map());

  const torchAudioSeqRef = useRef(0);
  const torchLoopWantedRef = useRef(false);
  const torchLoopFadeCancelRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Page visibility tracking and reusable audio helpers
  // When the page is not visible, we avoid scheduling background loops and
  // creating unnecessary audio objects. A simple visibilitystate watcher tracks
  // whether the page is currently visible. Additionally, reusable HTMLAudio
  // elements are cached to avoid repeated construction and allow one-shot
  // playback with consistent volume and preload settings.

  // Track whether the page is currently visible. Default to true on the server.
  const [pageVisible, setPageVisible] = useState(() => {
    if (typeof document === 'undefined') return true;
    return document.visibilityState !== 'hidden';
  });
  // Update visibility state on visibilitychange events.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handleVisibilityChange = () => {
      setPageVisible(document.visibilityState !== 'hidden');
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Cache of reusable HTMLAudio elements, keyed by slot name.
  const reusableHtmlAudioRef = useRef({});

  const getOrCreateHtmlAudio = (refObj, key, src, options = {}) => {
    let audio = (refObj.current && refObj.current[key]) || null;
    if (!audio) {
      audio = new Audio(src);
      if (!refObj.current) {
        refObj.current = {};
      }
      refObj.current[key] = audio;
    } else {
      try {
        // Use absolute URL comparison to detect source mismatch.
        const absolute = new URL(src, window.location.href).href;
        if (!audio.src || audio.src !== absolute) {
          audio.pause?.();
          audio = new Audio(src);
          refObj.current[key] = audio;
        }
      } catch {
        try {
          if (!audio.src || !String(audio.src).includes(src)) {
            audio.pause?.();
            audio = new Audio(src);
            refObj.current[key] = audio;
          }
        } catch {}
      }
    }
    try {
      audio.preload = options.preload ?? 'auto';
    } catch {}
    try {
      audio.loop = !!options.loop;
    } catch {}
    if (typeof options.volume === 'number') {
      try {
        audio.volume = options.volume;
      } catch {}
    }
    return audio;
  };

  const playReusableOneShot = async (key, src, options = {}) => {
    try {
      const audio = getOrCreateHtmlAudio(reusableHtmlAudioRef, key, src, {
        preload: 'auto',
        loop: false,
        volume: options.volume,
      });
      try {
        audio.currentTime = 0;
      } catch {}
      await audio.play();
    } catch {
      // ignore playback errors
    }
  };

  const clampUnit = (value) => Math.max(0, Math.min(1, value));

  const currentSanityPoints =
    Number.isFinite(sanity)
      ? sanity
      : Number.isFinite(maxSp) && maxSp > 0 && Number.isFinite(sanityPct)
        ? sanityPct * maxSp
        : Number.isFinite(sanityPct)
          ? sanityPct * 100
          : Infinity;

  const resolvedSanityPct =
    Number.isFinite(maxSp) && maxSp > 0 && Number.isFinite(currentSanityPoints)
      ? clampUnit(currentSanityPoints / maxSp)
      : Number.isFinite(sanityPct)
        ? clampUnit(sanityPct > 1 ? sanityPct / 100 : sanityPct)
        : Number.isFinite(currentSanityPoints)
          ? clampUnit(currentSanityPoints / 100)
          : 1;

  const sanityAudioPressure = Math.pow(1 - resolvedSanityPct, 1.85);
  const hudGlitchPressure = resolvedSanityPct < HUD_GLITCH_START_PCT
    ? clampUnit((HUD_GLITCH_START_PCT - resolvedSanityPct) / HUD_GLITCH_START_PCT)
    : 0;
  const isLowSanityAudioState = currentSanityPoints <= LOW_SANITY_AUDIO_SP_THRESHOLD;
  const isSpecialBossFloorAudioMuted = isSpecialBossFloorActive;

  // ---------------------------
  // Combat music (plays while CombatOverlay is active)
  // Stored as a singleton on window.__dd_combatMusic.
  // - Starts when combat becomes active (after bootReady)
  // - Stops when combat closes (after victory/defeat/reward screens)
  // ---------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;

    const TARGET_VOL = 0.55;
    const FADE_IN_MS = 900;
    const FADE_OUT_MS = 1200;

    const pickSrc = () => {
      const activeDungeonBase =
        window.__dd_currentDungeonMusicBase ||
        window.__dd_dungeonMusicLastBase ||
        'dungeon';

      const combatBase = getCombatBaseForDungeon(activeDungeonBase);
      return buildMusicSrc(combatBase);
    };

    const fadeTo = (a, toVol, durationMs, onDone) => {
      const fromVol = typeof a.volume === 'number' ? a.volume : 0;
      const start =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      let raf = 0;

      const tick = (now) => {
        const nowMs =
          typeof now === 'number'
            ? now
            : typeof performance !== 'undefined'
              ? performance.now()
              : Date.now();
        const t = Math.min(1, (nowMs - start) / Math.max(1, durationMs));
        const v = fromVol + (toVol - fromVol) * t;
        try {
          a.volume = Math.max(0, Math.min(1, v));
        } catch {}

        if (t < 1) {
          raf = requestAnimationFrame(tick);
          return;
        }
        if (typeof onDone === 'function') onDone();
      };

      raf = requestAnimationFrame(tick);
      return () => {
        if (raf) cancelAnimationFrame(raf);
      };
    };

    if (isCombatActive && !suppressCombatMusic) {
      let a = window.__dd_combatMusic;
      if (!a) {
        a = new Audio(pickSrc());
        a.loop = true;
        a.preload = 'auto';
        try {
          a.volume = 0.55;
        } catch {}
        window.__dd_combatMusic = a;
      }

      try {
        a.currentTime = 0;
      } catch {}

      Promise.resolve(a.play()).catch(() => {});

      const cancelFade = fadeTo(a, TARGET_VOL, FADE_IN_MS);
      return () => {
        if (typeof cancelFade === 'function') cancelFade();
      };
    }

    const a = window.__dd_combatMusic;
    if (!a) return;

    const cancelFade = fadeTo(a, 0, FADE_OUT_MS, () => {
      try {
        a.pause();
        a.currentTime = 0;
        a.volume = 0;
        a.src = '';
      } catch {}
      try {
        window.__dd_combatMusic = null;
      } catch {}
    });

    return () => {
      if (typeof cancelFade === 'function') cancelFade();
    };
  }, [bootReady, isCombatActive, suppressCombatMusic]);

  // ---------------------------
  // Audio unlock (required for autoplay on most browsers)
  // Marks audio as unlocked after the first user gesture.
  // ---------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (audioUnlocked) return;

    const unlock = () => {
      setAudioUnlocked(true);
    };

    window.addEventListener('pointerdown', unlock, {
      once: true,
      passive: true,
    });
    window.addEventListener('touchstart', unlock, {
      once: true,
      passive: true,
    });
    window.addEventListener('keydown', unlock, { once: true });

    return () => {
      try {
        window.removeEventListener('pointerdown', unlock);
      } catch {}
      try {
        window.removeEventListener('touchstart', unlock);
      } catch {}
      try {
        window.removeEventListener('keydown', unlock);
      } catch {}
    };
  }, [audioUnlocked]);

  // ---------------------------
  // Dungeon exploration music (chains random tracks while exploring)
  // - Starts after boot is ready AND audio is unlocked
  // - Stops when combat starts
  // - When a track ends, immediately picks another random track from the remaining pool
  // Stored as a singleton on window.__dd_dungeonMusic.
  // ---------------------------
  useEffect(() => {
  if (typeof window === 'undefined') return;
  if (!bootReady) return;
  if (!audioUnlocked) return;

  const TARGET_VOL = 0.28;
  const FADE_IN_MS = 900;
  const FADE_OUT_MS = 520;
  const specialFloorBossCombatActive = isSpecialFloorBossCombatActive();
  const shouldUseBossFloorExplorationMusic =
    isSpecialBossFloorActive &&
    !isLowSanityAudioState &&
    !specialFloorBossCombatActive;
  const shouldMuteExplorationMusic =
    (!shouldUseBossFloorExplorationMusic && isCombatActive) ||
    (!shouldUseBossFloorExplorationMusic && isSpecialBossFloorAudioMuted) ||
    isLowSanityAudioState;

  const pickTrackConfig = () => {
    if (shouldUseBossFloorExplorationMusic) {
      const base = BOSS_FLOOR_EXPLORATION_MUSIC_BASE;
      window.__dd_currentDungeonMusicBase = base;
      return {
        base,
        src: buildMusicSrc(base),
        loop: true,
      };
    }

    const base = pickNextDungeonBase();
    window.__dd_currentDungeonMusicBase = base;
    return {
      base,
      src: buildMusicSrc(base),
      loop: false,
    };
  };

  const fadeTo = (a, toVol, durationMs, onDone) => {
    const fromVol = typeof a.volume === 'number' ? a.volume : 0;
    const start =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    let raf = 0;

    const tick = (now) => {
      const nowMs =
        typeof now === 'number'
          ? now
          : typeof performance !== 'undefined'
            ? performance.now()
            : Date.now();
      const t = Math.min(1, (nowMs - start) / Math.max(1, durationMs));
      const v = fromVol + (toVol - fromVol) * t;
      try {
        a.volume = Math.max(0, Math.min(1, v));
      } catch {}

      if (t < 1) {
        raf = requestAnimationFrame(tick);
        return;
      }
      if (typeof onDone === 'function') onDone();
    };

    raf = requestAnimationFrame(tick);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  };

  if (shouldMuteExplorationMusic) {
    const a = window.__dd_dungeonMusic;
    if (!a) return;

    const cancelFade = fadeTo(a, 0, FADE_OUT_MS, () => {
      try {
        a.pause();
        a.currentTime = 0;
        a.volume = 0;
      } catch {}
    });

    return () => {
      if (typeof cancelFade === 'function') cancelFade();
    };
  }

  const trackConfig = pickTrackConfig();
  let a = window.__dd_dungeonMusic;
  let srcChanged = false;

  if (!a) {
    a = new Audio(trackConfig.src);
    a.preload = 'auto';
    try {
      a.volume = 0;
    } catch {}
    window.__dd_dungeonMusic = a;
    srcChanged = true;
  } else {
    try {
      const absoluteSrc = new URL(trackConfig.src, window.location.href).href;
      if (!a.src || a.src !== absoluteSrc) {
        a.pause();
        a.src = trackConfig.src;
        srcChanged = true;
      }
    } catch {
      try {
        if (!a.src || !String(a.src).includes(trackConfig.src)) {
          a.pause();
          a.src = trackConfig.src;
          srcChanged = true;
        }
      } catch {}
    }
  }

  try {
    a.loop = !!trackConfig.loop;
  } catch {}

  const playNextDungeonTrack = () => {
    if (window.__dd_combatMusic || isCombatActive || isSpecialBossFloorAudioMuted) return;
    if (shouldUseBossFloorExplorationMusic) return;

    const nextTrackConfig = pickTrackConfig();
    try {
      a.loop = !!nextTrackConfig.loop;
      a.src = nextTrackConfig.src;
      a.currentTime = 0;
    } catch {}

    Promise.resolve(a.play()).catch(() => {});
  };

  try {
    a.onended = trackConfig.loop ? null : playNextDungeonTrack;
    if (srcChanged) {
      a.currentTime = 0;
    }
  } catch {}

  if (srcChanged || a.paused) {
    Promise.resolve(a.play()).catch(() => {});
  }

  const cancelFade = fadeTo(a, TARGET_VOL, FADE_IN_MS);
  return () => {
    try {
      if (a.onended === playNextDungeonTrack) a.onended = null;
    } catch {}
    if (typeof cancelFade === 'function') cancelFade();
  };
}, [bootReady, audioUnlocked, isCombatActive, isSpecialBossFloorActive, isSpecialBossFloorAudioMuted, isLowSanityAudioState]);

  useEffect(() => {
    return () => {
      if (typeof window === 'undefined') return;
      const a = window.__dd_dungeonMusic;
      if (!a) return;
      try {
        a.pause();
        a.currentTime = 0;
        a.volume = 0;
        a.src = '';
      } catch {}
      try {
        window.__dd_dungeonMusic = null;
      } catch {}
    };
  }, []);

  // ---------------------------
  // Torch audio
  // ---------------------------
  const pickTorchSrc = (baseName) => {
    try {
      const test = document.createElement('audio');
      const oggOk =
        !!test.canPlayType &&
        test.canPlayType('audio/ogg; codecs="vorbis"') !== '';
      return oggOk ? `/audio/${baseName}.ogg` : `/audio/${baseName}.mp3`;
    } catch {
      return `/audio/${baseName}.mp3`;
    }
  };

  const fadeAudioTo = (a, toVol, durationMs, onDone) => {
    const fromVol = typeof a.volume === 'number' ? a.volume : 0;
    const start =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    let raf = 0;

    const tick = (now) => {
      const nowMs =
        typeof now === 'number'
          ? now
          : typeof performance !== 'undefined'
            ? performance.now()
            : Date.now();
      const t = Math.min(1, (nowMs - start) / Math.max(1, durationMs));
      const v = fromVol + (toVol - fromVol) * t;
      try {
        a.volume = Math.max(0, Math.min(1, v));
      } catch {}

      if (t < 1) {
        raf = requestAnimationFrame(tick);
        return;
      }
      if (typeof onDone === 'function') onDone();
    };

    raf = requestAnimationFrame(tick);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  };

  const stopTorchLoop = ({ immediate = false, release = false } = {}) => {
    if (typeof window === 'undefined') return;
    const a = window.__dd_torchLoop;
    if (!a) return;

    if (typeof torchLoopFadeCancelRef.current === 'function') {
      try {
        torchLoopFadeCancelRef.current();
      } catch {}
    }
    torchLoopFadeCancelRef.current = null;

    const finalize = () => {
      try {
        a.pause();
        a.currentTime = 0;
        a.volume = 0;
        if (release) a.src = '';
      } catch {}
      if (release) {
        try {
          window.__dd_torchLoop = null;
        } catch {}
      }
    };

    if (immediate) {
      finalize();
      return;
    }

    torchLoopFadeCancelRef.current = fadeAudioTo(a, 0, 260, finalize);
  };

  const ensureTorchLoop = ({ restart = false } = {}) => {
    if (typeof window === 'undefined') return;
    const src = pickTorchSrc('torch');

    let a = window.__dd_torchLoop;
    if (!a) {
      a = new Audio(src);
      a.loop = true;
      a.preload = 'auto';
      try {
        a.volume = 0;
      } catch {}
      window.__dd_torchLoop = a;
    } else {
      try {
        if (!a.src || !String(a.src).includes(src)) a.src = src;
      } catch {}
    }

    if (restart) {
      try {
        a.currentTime = 0;
      } catch {}
    }

    Promise.resolve(a.play()).catch(() => {});

    if (typeof torchLoopFadeCancelRef.current === 'function') {
      try {
        torchLoopFadeCancelRef.current();
      } catch {}
    }
    torchLoopFadeCancelRef.current = fadeAudioTo(a, 0.32, 320);
  };

const playTorchIgniteAndLoop = () => {
  if (typeof window === 'undefined') return;
  if (!bootReady) return;
  if (isCombatActive) return;

  torchLoopWantedRef.current = !isSpecialBossFloorAudioMuted;
  const seq = (torchAudioSeqRef.current += 1);
  
    stopTorchLoop({ immediate: true });

    const src = pickTorchSrc('torch_lit');
    let lit = window.__dd_torchLit;
    if (!lit) {
      lit = new Audio(src);
      lit.loop = false;
      lit.preload = 'auto';
      try {
        lit.volume = 0.9;
      } catch {}
      window.__dd_torchLit = lit;
    } else {
      try {
        if (!lit.src || !String(lit.src).includes(src)) lit.src = src;
      } catch {}
    }

    try {
      lit.onended = null;
    } catch {}

    lit.onended = () => {
      if (torchAudioSeqRef.current !== seq) return;
      if (!torchLoopWantedRef.current) return;
      ensureTorchLoop({ restart: true });
    };

    try {
      lit.currentTime = 0;
    } catch {}

    Promise.resolve(lit.play()).catch(() => {
      if (torchAudioSeqRef.current !== seq) return;
      if (!torchLoopWantedRef.current) return;
      ensureTorchLoop({ restart: true });
    });
  };

  const playBrazierLitSfx = () => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;
    if (isCombatActive) return;
    const src = '/audio/sound%20effects/Brazier_Lit.mp3';
    // Play the brazier-lit sound via reusable audio helper. Volume matches original.
    playReusableOneShot('brazierLit', src, { volume: 0.85 });
  };

  const pickStepSfxSrc = (idx) => {
    return `/audio/sound%20effects/Steps_${idx}.mp3`;
  };

  const primePlayerStepSfxPool = () => {
    if (typeof window === 'undefined') return;

    if (!window.__dd_playerStepSfxPool) {
      const VOICES_PER_STEP = 0;

      const byStep = {};
      for (let idx = 1; idx <= 4; idx++) {
        const voices = [];
        for (let v = 0; v < VOICES_PER_STEP; v++) {
          const a = new Audio(pickStepSfxSrc(idx));
          a.preload = 'auto';
          a.loop = false;
          try {
            a.volume = 0.55;
          } catch {}
          try {
            a.load?.();
          } catch {}
          voices.push(a);
        }
        byStep[idx] = { voices, cursor: 0 };
      }

      window.__dd_playerStepSfxPool = {
        byStep,
        lastIdx: 0,
      };
    }
  };

  const playPlayerStepSfx = () => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;
    if (isCombatActive) return;

    primePlayerStepSfxPool();

    const pool = window.__dd_playerStepSfxPool;
    if (!pool?.byStep) return;

    let idx = 1 + Math.floor(Math.random() * 4);
    if (idx === pool.lastIdx) {
      idx = (idx % 4) + 1;
    }
    pool.lastIdx = idx;

    const entry = pool.byStep[idx];
    if (!entry?.voices?.length) return;

    const a = entry.voices[entry.cursor];
    entry.cursor = (entry.cursor + 1) % entry.voices.length;

    try {
      a.pause();
      a.currentTime = 0;
      a.volume = 0.55;
    } catch {}

    Promise.resolve(a.play()).catch(() => {});
  };

  const pickTrapSfxSrc = (baseName) => {
    try {
      const test = document.createElement('audio');
      const oggOk =
        !!test.canPlayType &&
        test.canPlayType('audio/ogg; codecs="vorbis"') !== '';
      return oggOk
        ? `/audio/sound%20effects/${baseName}.ogg`
        : `/audio/sound%20effects/${baseName}.mp3`;
    } catch {
      return `/audio/sound%20effects/${baseName}.mp3`;
    }
  };

  const playTrapCountdownStartSfx = () => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;
    if (isCombatActive) return;
    const src = pickTrapSfxSrc('Spike_Trap_1');
    // Reuse the trap countdown start sound via helper. Volume matches original.
    playReusableOneShot('trapCountdownStart', src, { volume: 0.9 });
  };

  const playTrapActivateSfx = () => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;
    if (isCombatActive) return;
    const src = pickTrapSfxSrc('Spike_Trap_2');
    // Reuse the trap activation sound via helper. Volume matches original.
    playReusableOneShot('trapActivate', src, { volume: 0.95 });
  };

  const playChestOpenSfx = () => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;
    if (isCombatActive) return;
    const src = pickTrapSfxSrc('Chest_Open');
    // Reuse the chest open sound via helper.
    playReusableOneShot('chestOpen', src, { volume: 0.9 });
  };

  const playChestUnlockingSfx = () => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;
    if (isCombatActive) return;
    const src = pickTrapSfxSrc('Chest_Unlocking');
    // Reuse the chest unlocking sound via helper.
    playReusableOneShot('chestUnlocking', src, { volume: 0.9 });
  };

  const playDoorOpenSfx = () => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;
    if (isCombatActive) return;
    const src = pickTrapSfxSrc('Door_Open');
    // Reuse the door open sound via helper.
    playReusableOneShot('doorOpen', src, { volume: 0.9 });
  };

  const playDoorUnlockingSfx = () => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;
    if (isCombatActive) return;
    const src = pickTrapSfxSrc('Door_Unlocking_Mix');
    // Reuse the door unlocking sound via helper.
    playReusableOneShot('doorUnlocking', src, { volume: 0.9 });
  };

  const playLoreInspectSfx = () => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;
    if (isCombatActive) return;
    const src = pickTrapSfxSrc('Lore_1');
    // Reuse the lore inspect sound via helper.
    playReusableOneShot('loreInspect', src, { volume: 0.9 });
  };

  const playDoorBarredSfx = () => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;
    if (isCombatActive) return;
    const src = pickTrapSfxSrc('Door_Barred');
    // Reuse the door barred sound via helper.
    playReusableOneShot('doorBarred', src, { volume: 0.9 });
  };

  const playStepsDownSfx = () => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;
    if (isCombatActive) return;
    const src = pickTrapSfxSrc('Steps_Down');
    // Reuse the steps down sound via helper.
    playReusableOneShot('stepsDown', src, { volume: 0.9 });
  };

const playSpiderwebShuffleSfx = async () => {
  if (typeof window === 'undefined') return;
  if (!bootReady) return;
  if (!audioUnlocked) return;
  if (isCombatActive) return;
  if (isStorylineActive) return;
  if (!isSpiderBossFloorActive) return;

  const now = performance.now();
  const busyUntil = Number(window.__dd_webShuffleBusyUntil || 0);

  if (busyUntil > now) return;

  // Small temporary lock so rapid repeated calls while loading don't stack
  window.__dd_webShuffleBusyUntil = now + 250;

  const ctx = getAudioContext();
  if (!ctx) {
    window.__dd_webShuffleBusyUntil = 0;
    return;
  }

  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {}
  }

  const buffer = await getDecodedBuffer('/audio/sound%20effects/Web_Shuffle.mp3');
  if (!buffer) {
    window.__dd_webShuffleBusyUntil = 0;
    return;
  }

  const playbackRate = 0.96 + Math.random() * 0.1;
  const durationMs = Math.max(250, (buffer.duration / playbackRate) * 1000);

  // Lock for the real playback duration
  window.__dd_webShuffleBusyUntil = performance.now() + durationMs;

  try {
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = playbackRate;

    const gain = ctx.createGain();
    gain.gain.value = 0.52;

    if (typeof ctx.createStereoPanner === 'function') {
      const panner = ctx.createStereoPanner();
      panner.pan.value = -0.2 + Math.random() * 0.4;

      source.connect(gain);
      gain.connect(panner);
      panner.connect(ctx.destination);
    } else {
      source.connect(gain);
      gain.connect(ctx.destination);
    }

    source.onended = () => {
      const currentBusyUntil = Number(window.__dd_webShuffleBusyUntil || 0);
      if (currentBusyUntil <= performance.now() + 30) {
        window.__dd_webShuffleBusyUntil = 0;
      }
    };

    source.start(0);
  } catch {
    window.__dd_webShuffleBusyUntil = 0;
  }
};

  const triggerSecretWhisperTorchFlicker = () => {
    const torchProg = tarotNoTorchesNeeded
      ? 1
      : Math.max(0, 1 - turns / currentFloorTorchDuration);

    if (torchProg <= 0) return;

    const gustLeft = Math.random() < 0.5;
    const sign = gustLeft ? -1 : 1;

    const durationMs = 420 + Math.floor(Math.random() * 420);
    const steps = 5 + Math.floor(Math.random() * 4);

    const shiftX = `${(sign * (10 + Math.random() * 18)).toFixed(2)}px`;
    const shiftY = `${(-1 * (1 + Math.random() * 5)).toFixed(2)}px`;
    const rotate = `${(sign * (1.2 + Math.random() * 3.8)).toFixed(2)}deg`;

    const scaleMin = (0.975 + Math.random() * 0.015).toFixed(3);
    const scaleMax = (1.0 + Math.random() * 0.02).toFixed(3);

    const opacityMin = (0.18 + Math.random() * 0.1).toFixed(3);
    const opacityMax = (0.56 + Math.random() * 0.18).toFixed(3);

    const blurMin = `${(0.4 + Math.random() * 0.6).toFixed(2)}px`;
    const blurMax = `${(1.4 + Math.random() * 1.2).toFixed(2)}px`;

    const centerX = (50 + sign * (4 + Math.random() * 8)).toFixed(2);
    const centerY = (50 - Math.random() * 6).toFixed(2);

    const coreAlpha = (0.48 + Math.random() * 0.18).toFixed(3);
    const midAlpha = (0.2 + Math.random() * 0.12).toFixed(3);
    const outerAlpha = (0.08 + Math.random() * 0.1).toFixed(3);

    const next = {
      id: `${Date.now()}-${Math.random()}`,
      durationMs,
      steps,
      shiftX,
      shiftY,
      rotate,
      scaleMin,
      scaleMax,
      opacityMin,
      opacityMax,
      blurMin,
      blurMax,
      centerX,
      centerY,
      coreAlpha,
      midAlpha,
      outerAlpha,
    };

    setSecretWhisperTorchFlicker(next);

    if (secretWhisperTorchFlickerTimeoutRef.current) {
      clearTimeout(secretWhisperTorchFlickerTimeoutRef.current);
    }

    secretWhisperTorchFlickerTimeoutRef.current = setTimeout(() => {
      setSecretWhisperTorchFlicker(null);
      secretWhisperTorchFlickerTimeoutRef.current = null;
    }, durationMs + 120);
  };
  const triggerSanityScreenFlicker = () => {
    if (sanityTier <= 0) return;

    const durationMs = 120 + Math.floor(Math.random() * 180);

    const next = {
      id: `${Date.now()}-${Math.random()}`,
      durationMs,
      darknessAlpha: 0.04 + (madness || 0) * 0.10,
      violetAlpha: 0.05 + (madness || 0) * 0.16,
      blurPx: (0.6 + (madness || 0) * 1.8).toFixed(2),
      scale: (1 + (madness || 0) * 0.012).toFixed(3),
    };

    setSanityScreenFlicker(next);

    if (sanityScreenFlickerTimeoutRef.current) {
      clearTimeout(sanityScreenFlickerTimeoutRef.current);
    }

    sanityScreenFlickerTimeoutRef.current = setTimeout(() => {
      setSanityScreenFlicker(null);
      sanityScreenFlickerTimeoutRef.current = null;
    }, durationMs + 60);
  };

  const triggerSanityDistortionSpike = () => {
    if (sanityTier < 2) return;

    const durationMs = 180 + Math.floor(Math.random() * 220);

    const next = {
      id: `${Date.now()}-${Math.random()}`,
      durationMs,
      jitterX: ((Math.random() - 0.5) * (2 + (madness || 0) * 5)).toFixed(2),
      jitterY: ((Math.random() - 0.5) * (1 + (madness || 0) * 3)).toFixed(2),
      blurPx: (0.5 + (madness || 0) * 1.3).toFixed(2),
      brightness: (1.0 - (madness || 0) * 0.08).toFixed(3),
      saturate: (1.0 - (madness || 0) * 0.18).toFixed(3),
      scale: (1 + (madness || 0) * 0.014).toFixed(3),
    };

    setSanityDistortionSpike(next);

    if (sanityDistortionTimeoutRef.current) {
      clearTimeout(sanityDistortionTimeoutRef.current);
    }

    sanityDistortionTimeoutRef.current = setTimeout(() => {
      setSanityDistortionSpike(null);
      sanityDistortionTimeoutRef.current = null;
    }, durationMs + 50);
  };

  const triggerSanityPeripheralShadow = () => {
    if (sanityTier < 3) return;

    const sidePool = ['left', 'right', 'top', 'bottom'];
    const side = sidePool[Math.floor(Math.random() * sidePool.length)];
    const durationMs = 380 + Math.floor(Math.random() * 420);

    const next = {
      id: `${Date.now()}-${Math.random()}`,
      side,
      durationMs,
      opacity: 0.10 + (madness || 0) * 0.18,
      blurPx: (10 + (madness || 0) * 18).toFixed(2),
      driftPx: (20 + (madness || 0) * 40).toFixed(2),
      widthPct: (side === 'left' || side === 'right') ? 22 + Math.random() * 12 : 46 + Math.random() * 18,
      heightPct: (side === 'top' || side === 'bottom') ? 20 + Math.random() * 12 : 48 + Math.random() * 16,
    };

    setSanityPeripheralShadow(next);

    if (sanityPeripheralShadowTimeoutRef.current) {
      clearTimeout(sanityPeripheralShadowTimeoutRef.current);
    }

    sanityPeripheralShadowTimeoutRef.current = setTimeout(() => {
      setSanityPeripheralShadow(null);
      sanityPeripheralShadowTimeoutRef.current = null;
    }, durationMs + 80);
  };

const triggerSanityCollapseBurst = () => {
  if (sanityPct > 0) return;
  if (!bootReady) return;
  if (isCombatActive) return;
  if (isStorylineActive) return;

  const durationMs = 100 + Math.floor(Math.random() * 140);
  const sliceCount = 4 + Math.floor(Math.random() * 4);

  const slices = Array.from({ length: sliceCount }, () => ({
    topPct: Number((Math.random() * 100).toFixed(2)),
    heightPx: Number((6 + Math.random() * 18).toFixed(2)),
    shiftPx: Number((((Math.random() - 0.5) * 36)).toFixed(2)),
    opacity: Number((0.08 + Math.random() * 0.14).toFixed(3)),
  }));

  const next = {
    id: `${Date.now()}-${Math.random()}`,
    durationMs,
    strength: Number((0.72 + Math.random() * 0.28).toFixed(3)),
    chromaShiftPx: Number((1 + Math.random() * 2.5).toFixed(2)),
    darkFlashAlpha: Number((0.08 + Math.random() * 0.16).toFixed(3)),
    boardJitterX: Number((((Math.random() - 0.5) * 10)).toFixed(2)),
    boardJitterY: Number((((Math.random() - 0.5) * 4)).toFixed(2)),
    boardBlurPx: Number((0.8 + Math.random() * 1.6).toFixed(2)),
    boardScale: Number((1 + Math.random() * 0.018).toFixed(3)),
    boardSkewDeg: Number((((Math.random() - 0.5) * 2.2)).toFixed(2)),
    slices,
  };

  setSanityCollapseBurst(next);

  if (sanityCollapseBurstTimeoutRef.current) {
    clearTimeout(sanityCollapseBurstTimeoutRef.current);
  }

  sanityCollapseBurstTimeoutRef.current = setTimeout(() => {
    setSanityCollapseBurst(null);
    sanityCollapseBurstTimeoutRef.current = null;
  }, durationMs + 70);
};

const triggerSanityHudGlitchBurst = () => {
  if (hudGlitchPressure <= 0) return;
  if (!bootReady) return;
  if (isCombatActive) return;
  if (isStorylineActive) return;

  const instability = Math.max(0, Math.min(1, Number(hudGlitchPressure) || 0));
  const chaosBoost = 0.95 + instability * 0.7 + (sanityPct <= 0 ? 0.2 : 0);
  const durationBase = 120 + Math.floor(Math.random() * 130);
  const occasionalHold = Math.random() < (0.10 + instability * 0.24)
    ? 55 + Math.floor(Math.random() * 120)
    : 0;
  const durationMs = Math.round((durationBase + occasionalHold) * chaosBoost);

  const next = {
    id: `${Date.now()}-${Math.random()}`,
    durationMs,
    jitterX: Number((((Math.random() - 0.5) * (3 + instability * 9)) * chaosBoost).toFixed(2)),
    jitterY: Number((((Math.random() - 0.5) * (1.8 + instability * 4.8)) * chaosBoost).toFixed(2)),
    ghostOpacity: Number((0.12 + Math.random() * 0.14 + instability * 0.18).toFixed(3)),
    ghostShiftX: Number((((Math.random() - 0.5) * (3 + instability * 7)) * chaosBoost).toFixed(2)),
    ghostShiftY: Number((((Math.random() - 0.5) * (2 + instability * 4.5)) * chaosBoost).toFixed(2)),
    dimAlpha: Number((0.03 + Math.random() * 0.08 + instability * 0.12).toFixed(3)),
  };

  setSanityHudGlitchBurst(next);

  if (sanityHudGlitchBurstTimeoutRef.current) {
    clearTimeout(sanityHudGlitchBurstTimeoutRef.current);
  }

  sanityHudGlitchBurstTimeoutRef.current = setTimeout(() => {
    setSanityHudGlitchBurst(null);
    sanityHudGlitchBurstTimeoutRef.current = null;
  }, durationMs + 65);
};

  const playSanityWhisperSfx = () => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;
    if (!audioUnlocked) return;
    if (isCombatActive) return;
    if (isStorylineActive) return;
    if (isLowSanityAudioState) return;
    if (sanityTier <= 0) return;

    const now = Date.now();
    const minCooldown = sanityTier === 1 ? 9000 : sanityTier === 2 ? 6500 : 4200;
    if (now - sanityWhisperLastAtRef.current < minCooldown) {
      return;
    }

    let nextIdx = 1 + Math.floor(Math.random() * 12);
    if (nextIdx === sanityWhisperLastIdxRef.current) {
      nextIdx = (nextIdx % 12) + 1;
    }
    const src = `/audio/sound%20effects/Ghostly_${nextIdx}.mp3`;

    // Compute dynamic volume based on madness. Clamp to the original range.
    const vol = Math.max(0.08, Math.min(0.22, 0.08 + (madness || 0) * 0.14));

    sanityWhisperLastIdxRef.current = nextIdx;
    sanityWhisperLastAtRef.current = now;

    // Occasionally trigger the sanity screen flicker on whisper plays.
    if (Math.random() < 0.45 + (madness || 0) * 0.35) {
      triggerSanityScreenFlicker();
    }

    // Play the whisper via reusable helper.
    playReusableOneShot('sanityWhisper', src, { volume: vol });
  };

  const getAudioContext = () => {
    if (typeof window === 'undefined') return null;
    if (audioContextRef.current) return audioContextRef.current;

    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;

    audioContextRef.current = new Ctx();
    return audioContextRef.current;
  };

  const getDecodedBuffer = async (src) => {
    const cache = decodedBufferCacheRef.current;
    if (cache.has(src)) return cache.get(src);

    const ctx = getAudioContext();
    if (!ctx) return null;

    const res = await fetch(src);
    const arr = await res.arrayBuffer();
    const buf = await ctx.decodeAudioData(arr.slice(0));
    cache.set(src, buf);
    return buf;
  };

  const playPositionalHallucinationSfx = async ({
    src,
    volume = 0.32,
    motion = null,
  }) => {
    if (!bootReady) return;
    if (!audioUnlocked) return;
    if (isCombatActive) return;
    if (isStorylineActive) return;

    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {}
    }

    const buffer = await getDecodedBuffer(src);
    if (!buffer) return;

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const gain = ctx.createGain();
    const duration = Math.max(0.7, Number(buffer.duration) || 1.3);

    const presetPool = [
      { start: { x: -2.7, y: 0.08, z: -0.55 }, end: { x: 2.7, y: -0.04, z: -0.2 }, gainStart: 0.92, gainEnd: 1.0 },
      { start: { x: 2.7, y: -0.08, z: -0.55 }, end: { x: -2.7, y: 0.05, z: -0.2 }, gainStart: 0.92, gainEnd: 1.0 },
      { start: { x: 0.15, y: 0.02, z: 3.8 }, end: { x: -0.12, y: 0, z: -0.4 }, gainStart: 0.42, gainEnd: 1.0 },
      { start: { x: -0.25, y: 1.95, z: -0.6 }, end: { x: 0.3, y: -1.65, z: -0.15 }, gainStart: 0.74, gainEnd: 0.96 },
      { start: { x: -0.55, y: 0.22, z: 5.6 }, end: { x: 0.18, y: 0.02, z: -0.35 }, gainStart: 0.22, gainEnd: 1.0 },
      { start: { x: 1.75, y: 1.15, z: 2.8 }, end: { x: -1.2, y: -0.6, z: -0.4 }, gainStart: 0.35, gainEnd: 0.95 },
    ];

    const selectedMotion =
      motion || presetPool[Math.floor(Math.random() * presetPool.length)];

    const clamp01 = (value) => Math.max(0, Math.min(1, value));
    const setPannerPosition = (panner, x, y, z) => {
      if (panner.positionX && typeof panner.positionX.setValueAtTime === 'function') {
        panner.positionX.setValueAtTime(x, ctx.currentTime);
        panner.positionY.setValueAtTime(y, ctx.currentTime);
        panner.positionZ.setValueAtTime(z, ctx.currentTime);
        return;
      }

      if (typeof panner.setPosition === 'function') {
        panner.setPosition(x, y, z);
      }
    };

    const startPoint = selectedMotion.start;
    const endPoint = selectedMotion.end;
    const gainStart = Number.isFinite(selectedMotion.gainStart) ? selectedMotion.gainStart : 0.8;
    const gainEnd = Number.isFinite(selectedMotion.gainEnd) ? selectedMotion.gainEnd : 1;
    gain.gain.value = Math.max(0, volume * gainStart);

    const entry = { source, gain, panner: null, raf: 0 };

    if (typeof ctx.createPanner === 'function') {
      const panner = ctx.createPanner();
      entry.panner = panner;
      panner.panningModel = 'HRTF';
      panner.distanceModel = 'inverse';
      panner.refDistance = 1;
      panner.maxDistance = 16;
      panner.rolloffFactor = 1.3;
      panner.coneInnerAngle = 360;
      panner.coneOuterAngle = 0;
      panner.coneOuterGain = 0;

      source.connect(gain);
      gain.connect(panner);
      panner.connect(ctx.destination);

      setPannerPosition(panner, startPoint.x, startPoint.y, startPoint.z);

      const startAt = performance.now();
      const tick = (now) => {
        const t = clamp01((now - startAt) / Math.max(1, duration * 1000));
        const x = startPoint.x + (endPoint.x - startPoint.x) * t;
        const y = startPoint.y + (endPoint.y - startPoint.y) * t;
        const z = startPoint.z + (endPoint.z - startPoint.z) * t;
        setPannerPosition(panner, x, y, z);

        const nextGain = volume * (gainStart + (gainEnd - gainStart) * t);
        if (gain.gain && typeof gain.gain.setValueAtTime === 'function') {
          gain.gain.setValueAtTime(Math.max(0, nextGain), ctx.currentTime);
        } else {
          gain.gain.value = Math.max(0, nextGain);
        }

        if (t < 1) {
          entry.raf = requestAnimationFrame(tick);
        }
      };

      entry.raf = requestAnimationFrame(tick);
    } else if (typeof ctx.createStereoPanner === 'function') {
      const panner = ctx.createStereoPanner();
      entry.panner = panner;
      source.connect(gain);
      gain.connect(panner);
      panner.connect(ctx.destination);

      const startPan = Math.max(-1, Math.min(1, startPoint.x / 3));
      const endPan = Math.max(-1, Math.min(1, endPoint.x / 3));
      panner.pan.setValueAtTime(startPan, ctx.currentTime);
      panner.pan.linearRampToValueAtTime(endPan, ctx.currentTime + duration);
      gain.gain.setValueAtTime(Math.max(0, volume * gainStart), ctx.currentTime);
      gain.gain.linearRampToValueAtTime(Math.max(0, volume * gainEnd), ctx.currentTime + duration);
    } else {
      source.connect(gain);
      gain.connect(ctx.destination);
    }

    source.onended = () => {
      if (entry.raf) {
        try {
          cancelAnimationFrame(entry.raf);
        } catch {}
      }
      try {
        source.disconnect();
      } catch {}
      try {
        gain.disconnect();
      } catch {}
      try {
        entry.panner?.disconnect?.();
      } catch {}
      removeMovingSanityNode(entry);
    };

    movingSanityActiveNodesRef.current.push(entry);
    source.start(0);
  };

const removeMovingSanityNode = (entry) => {
  movingSanityActiveNodesRef.current = movingSanityActiveNodesRef.current.filter(
    (item) => item !== entry
  );
};

const stopMovingSanityNodes = () => {
  const active = movingSanityActiveNodesRef.current;
  movingSanityActiveNodesRef.current = [];

  active.forEach((entry) => {
    try {
      entry?.source?.stop(0);
    } catch {}
    try {
      entry?.source?.disconnect?.();
    } catch {}
    try {
      entry?.gain?.disconnect?.();
    } catch {}
    try {
      entry?.panner?.disconnect?.();
    } catch {}
    if (entry?.raf) {
      try {
        cancelAnimationFrame(entry.raf);
      } catch {}
    }
  });
};

const removeSpiderCrawlNode = (entry) => {
  spiderCrawlActiveNodesRef.current = spiderCrawlActiveNodesRef.current.filter(
    (item) => item !== entry
  );
};

const stopSpiderCrawlNodes = () => {
  const active = spiderCrawlActiveNodesRef.current;
  spiderCrawlActiveNodesRef.current = [];

  active.forEach((entry) => {
    try {
      entry?.source?.stop(0);
    } catch {}
    try {
      entry?.source?.disconnect?.();
    } catch {}
    try {
      entry?.gain?.disconnect?.();
    } catch {}
    try {
      entry?.panner?.disconnect?.();
    } catch {}
    if (entry?.raf) {
      try {
        cancelAnimationFrame(entry.raf);
      } catch {}
    }
  });
};

const pickSpiderCrawlSrc = () => {
  let nextIdx = 1 + Math.floor(Math.random() * 5);
  if (nextIdx === spiderCrawlLastIdxRef.current) {
    nextIdx = (nextIdx % 5) + 1;
  }
  spiderCrawlLastIdxRef.current = nextIdx;
  return `/audio/sound%20effects/Spider_Legs${nextIdx}.mp3`;
};

const playSpiderCrawlSfx = async () => {
  if (!bootReady) return;
  if (!audioUnlocked) return;
  if (isCombatActive) return;
  if (isStorylineActive) return;
  if (!isSpiderBossFloorActive) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {}
  }

  const buffer = await getDecodedBuffer(pickSpiderCrawlSrc());
  if (!buffer) return;

  const source = ctx.createBufferSource();
  source.buffer = buffer;

  const gain = ctx.createGain();
  gain.gain.value =
    SPIDER_CRAWL_MIN_VOLUME +
    Math.random() * (SPIDER_CRAWL_MAX_VOLUME - SPIDER_CRAWL_MIN_VOLUME);

  const entry = { source, gain, panner: null, raf: 0 };
  const radius =
    SPIDER_CRAWL_MIN_RADIUS +
    Math.random() * (SPIDER_CRAWL_MAX_RADIUS - SPIDER_CRAWL_MIN_RADIUS);
  const startAngle = Math.random() * Math.PI * 2;
  const endAngle =
    startAngle +
    (0.7 + Math.random() * 1.15) * (Math.random() < 0.5 ? -1 : 1);
  const y = -0.12 + Math.random() * 0.24;
  const duration = Math.max(0.9, Number(buffer.duration) || 1.6);

  const connectDry = () => {
    source.connect(gain);
    gain.connect(ctx.destination);
  };

  if (typeof ctx.createPanner === 'function') {
    const panner = ctx.createPanner();
    entry.panner = panner;
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = 1;
    panner.maxDistance = 12;
    panner.rolloffFactor = 1.15;
    panner.coneInnerAngle = 360;
    panner.coneOuterAngle = 0;
    panner.coneOuterGain = 0;

    source.connect(gain);
    gain.connect(panner);
    panner.connect(ctx.destination);

    const updatePanner = (angle) => {
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;

      if (panner.positionX && typeof panner.positionX.setValueAtTime === 'function') {
        panner.positionX.setValueAtTime(x, ctx.currentTime);
        panner.positionY.setValueAtTime(y, ctx.currentTime);
        panner.positionZ.setValueAtTime(z, ctx.currentTime);
        return;
      }

      if (typeof panner.setPosition === 'function') {
        panner.setPosition(x, y, z);
      }
    };

    updatePanner(startAngle);

    const startAt = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - startAt) / Math.max(1, duration * 1000));
      updatePanner(startAngle + (endAngle - startAngle) * t);
      if (t < 1) {
        entry.raf = requestAnimationFrame(tick);
      }
    };
    entry.raf = requestAnimationFrame(tick);
  } else if (typeof ctx.createStereoPanner === 'function') {
    const panner = ctx.createStereoPanner();
    entry.panner = panner;
    source.connect(gain);
    gain.connect(panner);
    panner.connect(ctx.destination);
    const startPan = Math.max(-1, Math.min(1, Math.cos(startAngle) * 0.92));
    const endPan = Math.max(-1, Math.min(1, Math.cos(endAngle) * 0.92));
    panner.pan.setValueAtTime(startPan, ctx.currentTime);
    panner.pan.linearRampToValueAtTime(endPan, ctx.currentTime + duration);
  } else {
    connectDry();
  }

  source.onended = () => {
    if (entry.raf) {
      try {
        cancelAnimationFrame(entry.raf);
      } catch {}
    }
    try {
      source.disconnect();
    } catch {}
    try {
      gain.disconnect();
    } catch {}
    try {
      entry.panner?.disconnect?.();
    } catch {}
    removeSpiderCrawlNode(entry);
  };

  spiderCrawlActiveNodesRef.current.push(entry);
  source.start(0);
};

  const playSecretEntranceWhisperSfx = () => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;
    if (!audioUnlocked) return;
    if (isCombatActive) return;

    const now = Date.now();
    if (now - secretWhisperLastAtRef.current < SECRET_ENTRANCE_WHISPER_COOLDOWN_MS) {
      return;
    }

    let nextIdx = 1 + Math.floor(Math.random() * 12);
    if (nextIdx === secretWhisperLastIdxRef.current) {
      nextIdx = (nextIdx % 12) + 1;
    }
    const src = `/audio/sound%20effects/Ghostly_${nextIdx}.mp3`;

    secretWhisperLastIdxRef.current = nextIdx;
    secretWhisperLastAtRef.current = now;

    // Trigger the torch flicker effect on secret whispers.
    triggerSecretWhisperTorchFlicker();

    // Play the secret entrance whisper via reusable helper.
    playReusableOneShot('secretEntranceWhisper', src, { volume: SECRET_ENTRANCE_WHISPER_VOLUME });
  };

  useEffect(() => {
    if (!secretEntrancePos) return;
    if (!bootReady) return;
    if (!audioUnlocked) return;
    if (isCombatActive) return;

    const dx = playerPos.x - secretEntrancePos.x;
    const dy = playerPos.y - secretEntrancePos.y;

    const distSq = dx * dx + dy * dy;
    const radiusSq =
      SECRET_ENTRANCE_WHISPER_RADIUS_TILES *
      SECRET_ENTRANCE_WHISPER_RADIUS_TILES;

    if (distSq <= radiusSq) {
      playSecretEntranceWhisperSfx();
    }
  }, [bootReady, audioUnlocked, isCombatActive, playerPos, secretEntrancePos]);

  useEffect(() => {
    return () => {
      if (typeof window === 'undefined') return;
      const a = window.__dd_secretEntranceWhisperSfx;
      if (!a) return;

      try {
        a.pause();
        a.currentTime = 0;
        a.volume = 0;
        a.src = '';
      } catch {}

      try {
        window.__dd_secretEntranceWhisperSfx = null;
      } catch {}
    };
  }, []);

  useEffect(() => {
    return () => {
      if (secretWhisperTorchFlickerTimeoutRef.current) {
        clearTimeout(secretWhisperTorchFlickerTimeoutRef.current);
        secretWhisperTorchFlickerTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!bootReady) return;

    const torchProg = tarotNoTorchesNeeded
      ? 1
      : Math.max(0, 1 - turns / currentFloorTorchDuration);

const shouldLoop = !isCombatActive && !isSpecialBossFloorAudioMuted && torchProg > 0;
    torchLoopWantedRef.current = shouldLoop;

if (!shouldLoop) {
  stopTorchLoop({ immediate: false });

  if (!isSpecialBossFloorAudioMuted) {
    const lit = window.__dd_torchLit;
    if (lit) {
      try {
        lit.pause();
        lit.currentTime = 0;
      } catch {}
    }
  }

  return;
}

    const lit = window.__dd_torchLit;
    const ignitePlaying = !!(lit && !lit.paused && !lit.ended);
    if (!ignitePlaying) ensureTorchLoop({ restart: false });
  }, [
    bootReady,
    isCombatActive,
    isSpecialBossFloorAudioMuted,
    tarotNoTorchesNeeded,
    turns,
    currentFloorTorchDuration,
  ]);

  useEffect(() => {
    return () => {
      torchLoopWantedRef.current = false;
      stopTorchLoop({ immediate: true, release: true });

      if (typeof window !== 'undefined') {
        const lit = window.__dd_torchLit;
        if (lit) {
          try {
            lit.pause();
            lit.currentTime = 0;
            lit.src = '';
          } catch {}
        }
        try {
          window.__dd_torchLit = null;
        } catch {}
      }
    };
  }, []);

  
  // ---------------------------
  // Sanity loops (exploration only)
  // ---------------------------
  useEffect(() => {
    if (!bootReady) return;
    if (!audioUnlocked) return;
    if (!pageVisible) return;
    if (isCombatActive) return;
    if (isStorylineActive) return;
    if (isLowSanityAudioState) return;
    if (sanityTier <= 0) return;

    let cancelled = false;

    const schedule = () => {
      const baseDelay =
        sanityTier === 1 ? 14000 :
        sanityTier === 2 ? 9000 :
        6000;

      const jitter = 1500 + Math.random() * 3500;

      sanityWhisperLoopTimeoutRef.current = setTimeout(() => {
        if (cancelled) return;
        playSanityWhisperSfx();
        schedule();
      }, baseDelay + jitter);
    };

    schedule();

    return () => {
      cancelled = true;
      if (sanityWhisperLoopTimeoutRef.current) {
        clearTimeout(sanityWhisperLoopTimeoutRef.current);
        sanityWhisperLoopTimeoutRef.current = null;
      }
    };
  }, [
    bootReady,
    audioUnlocked,
    pageVisible,
    isCombatActive,
    isStorylineActive,
    sanityTier,
    madness,
    isLowSanityAudioState,
  ]);

  useEffect(() => {
    if (!bootReady) return;
    if (!pageVisible) return;
    if (isCombatActive) return;
    if (isStorylineActive) return;
    if (sanityTier <= 0) return;

    let cancelled = false;

    const schedule = () => {
      const baseDelay =
        sanityTier === 1 ? 18000 :
        sanityTier === 2 ? 11000 :
        7500;

      const jitter = 1200 + Math.random() * 2800;

      sanityFlickerLoopTimeoutRef.current = setTimeout(() => {
        if (cancelled) return;
        triggerSanityScreenFlicker();
        schedule();
      }, baseDelay + jitter);
    };

    schedule();

    return () => {
      cancelled = true;
      if (sanityFlickerLoopTimeoutRef.current) {
        clearTimeout(sanityFlickerLoopTimeoutRef.current);
        sanityFlickerLoopTimeoutRef.current = null;
      }
    };
  }, [
    bootReady,
    pageVisible,
    isCombatActive,
    isStorylineActive,
    sanityTier,
    madness,
  ]);

  useEffect(() => {
    if (!bootReady) return;
    if (!pageVisible) return;
    if (isCombatActive) return;
    if (isStorylineActive) return;
    if (sanityTier < 2) return;

    let cancelled = false;

    const schedule = () => {
      const baseDelay = sanityTier === 2 ? 12500 : 8000;
      const jitter = 2000 + Math.random() * 3200;

      sanityDistortionLoopTimeoutRef.current = setTimeout(() => {
        if (cancelled) return;
        triggerSanityDistortionSpike();
        schedule();
      }, baseDelay + jitter);
    };

    schedule();

    return () => {
      cancelled = true;
      if (sanityDistortionLoopTimeoutRef.current) {
        clearTimeout(sanityDistortionLoopTimeoutRef.current);
        sanityDistortionLoopTimeoutRef.current = null;
      }
    };
  }, [
    bootReady,
    pageVisible,
    isCombatActive,
    isStorylineActive,
    sanityTier,
    madness,
  ]);

useEffect(() => {
  if (spiderCrawlLoopTimeoutRef.current) {
    clearTimeout(spiderCrawlLoopTimeoutRef.current);
    spiderCrawlLoopTimeoutRef.current = null;
  }

  if (!bootReady || !audioUnlocked || !pageVisible || isCombatActive || isStorylineActive || !isSpiderBossFloorActive) {
    stopSpiderCrawlNodes();
    return undefined;
  }

  let cancelled = false;

  const schedule = (delayOverride = null) => {
    const delay =
      delayOverride ??
      (SPIDER_CRAWL_MIN_DELAY_MS +
        Math.random() * (SPIDER_CRAWL_MAX_DELAY_MS - SPIDER_CRAWL_MIN_DELAY_MS));

    spiderCrawlLoopTimeoutRef.current = setTimeout(async () => {
      if (cancelled) return;

      try {
        await playSpiderCrawlSfx();
      } catch {}

      schedule();
    }, delay);
  };

  schedule(700 + Math.random() * 900);

  return () => {
    cancelled = true;
    if (spiderCrawlLoopTimeoutRef.current) {
      clearTimeout(spiderCrawlLoopTimeoutRef.current);
      spiderCrawlLoopTimeoutRef.current = null;
    }
    stopSpiderCrawlNodes();
  };
}, [
  bootReady,
  audioUnlocked,
  pageVisible,
  isCombatActive,
  isStorylineActive,
  isSpiderBossFloorActive,
]);

useEffect(() => {
    if (!bootReady) return;
    if (!pageVisible) return;
    if (isCombatActive) return;
    if (isStorylineActive) return;
    if (hudGlitchPressure <= 0) return;

  let cancelled = false;

  const getDelayMs = () => {
    const currentPressure = Math.max(0, Math.min(1, Number(hudGlitchPressure) || 0));
    const clusterChance = Math.max(0.04, Math.min(0.34, 0.05 + currentPressure * 0.29));

    if (Math.random() < clusterChance) {
      const clusterMin = 700 + (1 - currentPressure) * 1850;
      const clusterMax = 1200 + (1 - currentPressure) * 2150;
      return clusterMin + Math.random() * Math.max(120, clusterMax - clusterMin);
    }

    const minDelay = 900 + (1 - currentPressure) * 4600;
    const maxDelay = 1800 + (1 - currentPressure) * 6200;
    return minDelay + Math.random() * Math.max(180, maxDelay - minDelay);
  };

  const schedule = (delay = getDelayMs()) => {
    sanityHudGlitchLoopTimeoutRef.current = setTimeout(() => {
      if (cancelled) return;
      triggerSanityHudGlitchBurst();
      schedule();
    }, delay);
  };

  schedule(getDelayMs());

  return () => {
    cancelled = true;
    if (sanityHudGlitchLoopTimeoutRef.current) {
      clearTimeout(sanityHudGlitchLoopTimeoutRef.current);
      sanityHudGlitchLoopTimeoutRef.current = null;
    }
  };
  }, [
    bootReady,
    pageVisible,
    isCombatActive,
    isStorylineActive,
    hudGlitchPressure,
  ]);

useEffect(() => {
  if (!bootReady) return;
  if (!audioUnlocked) return;
  if (!pageVisible) return;
  if (isCombatActive) return;
  if (isStorylineActive) return;
  if (sanityPct > 0) return;

  let cancelled = false;

  const schedule = () => {
    const baseDelay = 900;
    const jitter = 500 + Math.random() * 1400;

    sanityCollapseBurstLoopTimeoutRef.current = setTimeout(() => {
      if (cancelled) return;

      triggerSanityCollapseBurst();

      if (Math.random() < 0.72) {
        triggerSanityHudGlitchBurst();
      }

      schedule();
    }, baseDelay + jitter);
  };

  schedule();

  return () => {
    cancelled = true;
    if (sanityCollapseBurstLoopTimeoutRef.current) {
      clearTimeout(sanityCollapseBurstLoopTimeoutRef.current);
      sanityCollapseBurstLoopTimeoutRef.current = null;
    }
  };
}, [
  bootReady,
  pageVisible,
  isCombatActive,
  isStorylineActive,
  sanityPct,
]);

  useEffect(() => {
    if (!bootReady) return;
    if (!pageVisible) return;
    if (isCombatActive) return;
    if (isStorylineActive) return;
    if (sanityTier < 3) return;

    let cancelled = false;

    const schedule = () => {
      const baseDelay = 9500;
      const jitter = 2500 + Math.random() * 5000;

      sanityPeripheralShadowLoopTimeoutRef.current = setTimeout(() => {
        if (cancelled) return;
        triggerSanityPeripheralShadow();
        schedule();
      }, baseDelay + jitter);
    };

    schedule();

    return () => {
      cancelled = true;
      if (sanityPeripheralShadowLoopTimeoutRef.current) {
        clearTimeout(sanityPeripheralShadowLoopTimeoutRef.current);
        sanityPeripheralShadowLoopTimeoutRef.current = null;
      }
    };
  }, [
    bootReady,
    pageVisible,
    isCombatActive,
    isStorylineActive,
    sanityTier,
    madness,
  ]);

  useEffect(() => {
    if (!bootReady) return;
    if (!audioUnlocked) return;
    // When the page is hidden, avoid scheduling positional hallucination ambience.
    // Stop any active moving sanity nodes immediately and bail out.
    if (!pageVisible) {
      stopMovingSanityNodes();
      return;
    }
    if (isCombatActive) return;
    if (isStorylineActive) return;

    let cancelled = false;

    const pickSanityAmbienceSrc = () => {
      let nextIdx = 1 + Math.floor(Math.random() * SANITY_AMBIENCE_POOL_SIZE);
      if (nextIdx === lowSanitySfxLastIdxRef.current) {
        nextIdx = (nextIdx % SANITY_AMBIENCE_POOL_SIZE) + 1;
      }
      lowSanitySfxLastIdxRef.current = nextIdx;
      return `/audio/sanity/sanity${nextIdx}.mp3`;
    };

    const getDelayMs = () => {
      const currentPct = clampUnit(resolvedSanityPct);
      const anchors = SANITY_AMBIENCE_DELAY_ANCHORS;

      let upper = anchors[0];
      let lower = anchors[anchors.length - 1];

      for (let i = 0; i < anchors.length - 1; i += 1) {
        const start = anchors[i];
        const end = anchors[i + 1];

        if (currentPct <= start.pct && currentPct >= end.pct) {
          upper = start;
          lower = end;
          break;
        }
      }

      const span = Math.max(0.0001, upper.pct - lower.pct);
      const t = clampUnit((upper.pct - currentPct) / span);
      const easedT = Math.pow(t, currentPct <= 0.25 ? 0.92 : 1.04);

      let minDelay = upper.minMs + (lower.minMs - upper.minMs) * easedT;
      let maxDelay = upper.maxMs + (lower.maxMs - upper.maxMs) * easedT;

      const instability = clampUnit(Number(madness) || 0);
      const pressure = clampUnit(sanityAudioPressure);
      const range = Math.max(1200, maxDelay - minDelay);
      const compression = pressure * instability * 0.12;

      minDelay = Math.max(4000, minDelay * (1 - compression));
      maxDelay = Math.max(minDelay + 750, maxDelay * (1 - compression * 0.7));

      if (pressure > 0.9 && Math.random() < 0.08 + instability * 0.08) {
        return Math.max(4000, minDelay * (0.82 + Math.random() * 0.12));
      }

      return minDelay + Math.random() * Math.max(1000, Math.min(24000, range));
    };

    const getVolume = () => {
      const pressure = clampUnit(sanityAudioPressure);
      return Math.max(0.035, Math.min(0.24, 0.035 + pressure * 0.11 + (madness || 0) * 0.1));
    };

    const schedule = (delay = getDelayMs()) => {
      positionalHallucinationLoopTimeoutRef.current = setTimeout(async () => {
        if (cancelled) return;

        try {
          await playPositionalHallucinationSfx({
            src: pickSanityAmbienceSrc(),
            volume: getVolume(),
          });
        } catch {}

        schedule();
      }, delay);
    };

    schedule(getDelayMs());

    return () => {
      cancelled = true;
      if (positionalHallucinationLoopTimeoutRef.current) {
        clearTimeout(positionalHallucinationLoopTimeoutRef.current);
        positionalHallucinationLoopTimeoutRef.current = null;
      }
      stopMovingSanityNodes();
    };
  }, [
    bootReady,
    audioUnlocked,
    pageVisible,
    isCombatActive,
    isStorylineActive,
    sanityAudioPressure,
    madness,
  ]);

  // Cleanup sanity singletons / timeouts
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined') {
        const a = window.__dd_sanityWhisperSfx;
        if (a) {
          try {
            a.pause();
            a.currentTime = 0;
            a.volume = 0;
            a.src = '';
          } catch {}
          try {
            window.__dd_sanityWhisperSfx = null;
          } catch {}
        }
      }

      if (sanityScreenFlickerTimeoutRef.current) {
        clearTimeout(sanityScreenFlickerTimeoutRef.current);
        sanityScreenFlickerTimeoutRef.current = null;
      }
      if (sanityWhisperLoopTimeoutRef.current) {
        clearTimeout(sanityWhisperLoopTimeoutRef.current);
        sanityWhisperLoopTimeoutRef.current = null;
      }
      if (sanityFlickerLoopTimeoutRef.current) {
        clearTimeout(sanityFlickerLoopTimeoutRef.current);
        sanityFlickerLoopTimeoutRef.current = null;
      }
      if (sanityDistortionTimeoutRef.current) {
        clearTimeout(sanityDistortionTimeoutRef.current);
        sanityDistortionTimeoutRef.current = null;
      }
      if (sanityDistortionLoopTimeoutRef.current) {
        clearTimeout(sanityDistortionLoopTimeoutRef.current);
        sanityDistortionLoopTimeoutRef.current = null;
      }
      if (sanityPeripheralShadowTimeoutRef.current) {
        clearTimeout(sanityPeripheralShadowTimeoutRef.current);
        sanityPeripheralShadowTimeoutRef.current = null;
      }
      if (sanityPeripheralShadowLoopTimeoutRef.current) {
        clearTimeout(sanityPeripheralShadowLoopTimeoutRef.current);
        sanityPeripheralShadowLoopTimeoutRef.current = null;
      }
      if (positionalHallucinationLoopTimeoutRef.current) {
        clearTimeout(positionalHallucinationLoopTimeoutRef.current);
        positionalHallucinationLoopTimeoutRef.current = null;
      }
      stopMovingSanityNodes();
      if (sanityCollapseBurstTimeoutRef.current) {
      clearTimeout(sanityCollapseBurstTimeoutRef.current);
      sanityCollapseBurstTimeoutRef.current = null;
      }
      if (sanityCollapseBurstLoopTimeoutRef.current) {
      clearTimeout(sanityCollapseBurstLoopTimeoutRef.current);
      sanityCollapseBurstLoopTimeoutRef.current = null;
      }
      if (sanityHudGlitchBurstTimeoutRef.current) {
      clearTimeout(sanityHudGlitchBurstTimeoutRef.current);
      sanityHudGlitchBurstTimeoutRef.current = null;
      }
      if (sanityHudGlitchLoopTimeoutRef.current) {
      clearTimeout(sanityHudGlitchLoopTimeoutRef.current);
      sanityHudGlitchLoopTimeoutRef.current = null;
      }
    };
  }, []);

return {
    playTorchIgniteAndLoop,
    playBrazierLitSfx,
    playPlayerStepSfx,
    playTrapCountdownStartSfx,
    playTrapActivateSfx,
    playChestOpenSfx,
    playChestUnlockingSfx,
    playDoorOpenSfx,
    playDoorUnlockingSfx,
    playLoreInspectSfx,
    playDoorBarredSfx,
    playStepsDownSfx,
    playSpiderwebShuffleSfx,
    secretWhisperTorchFlicker,
    sanityScreenFlicker,
    sanityDistortionSpike,
    sanityPeripheralShadow,
    sanityCollapseBurst,
    sanityHudGlitchBurst,
  };
}
